import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { createMembershipCheckout } from '../../api';
import { useAuth } from '../../contexts/AuthContext';
import paymentGateway, { isPaymentInitializationTimeout } from '../../services/paymentGateway';
import './AuthPages.css';

const MEMBERSHIP_CHECKOUT_KEY = 'guhso-membership-checkout';

const PLAN_COPY = {
  monthly: {
    name: 'Monthly Membership',
    price: '$3.99',
    cadence: '/month',
  },
  annual: {
    name: 'Yearly Membership',
    price: '$38.50',
    cadence: '/year',
  },
};

const MembershipCheckoutPage = () => {
  const navigate = useNavigate();
  const { setAuthToken } = useAuth();
  const [checkoutData, setCheckoutData] = useState(null);
  const [paymentCard, setPaymentCard] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [statusMessage, setStatusMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [sdkReady, setSdkReady] = useState(false);
  const [paymentProvider, setPaymentProvider] = useState(null);
  const [initializationAttempt, setInitializationAttempt] = useState(0);
  const [canRetryPayment, setCanRetryPayment] = useState(false);
  const cardContainerRef = useRef(null);

  useEffect(() => {
    const raw = window.sessionStorage.getItem(MEMBERSHIP_CHECKOUT_KEY);

    if (!raw) {
      navigate('/register', { replace: true });
      return;
    }

    try {
      setCheckoutData(JSON.parse(raw));
    } catch {
      window.sessionStorage.removeItem(MEMBERSHIP_CHECKOUT_KEY);
      navigate('/register', { replace: true });
    }
  }, [navigate]);

  // The gateway service loads whichever SDK the backend has live, so this page
  // stays provider-agnostic.
  useEffect(() => {
    if (!checkoutData || paymentCard || !cardContainerRef.current) {
      return undefined;
    }

    let cancelled = false;

    const initCard = async () => {
      try {
        setErrorMessage('');
        setCanRetryPayment(false);
        // Resolve the provider first — the two SDKs take different style
        // schemas, so the theme has to be chosen before mounting.
        const { provider } = await paymentGateway.loadConfig();

        const handle = await paymentGateway.mountCard('membership-card-container',
          provider === 'stripe'
            ? {
              style: {
                base: {
                  color: '#ffffff',
                  fontSize: '16px',
                  '::placeholder': { color: '#8b8b99' },
                },
              },
            }
            : {
              style: {
                '.input-container': {
                  borderColor: '#3b3b48',
                  borderRadius: '14px',
                  backgroundColor: '#111116',
                },
                '.input-container.is-focus': {
                  borderColor: '#f7d135',
                },
                input: {
                  color: '#ffffff',
                  backgroundColor: '#111116',
                },
              },
            });

        if (cancelled) {
          paymentGateway.destroyCard(handle);
          return;
        }

        setPaymentCard(handle);
        setPaymentProvider(provider);
        setSdkReady(true);
      } catch (error) {
        if (!cancelled) {
          setSdkReady(false);
          setCanRetryPayment(true);
          setErrorMessage(isPaymentInitializationTimeout(error)
            ? 'The secure payment form took too long to load. Check your connection, then retry.'
            : error.message || 'We could not load the secure payment form. Please retry.');
        }
      }
    };

    initCard();

    return () => {
      cancelled = true;
    };
  }, [checkoutData, paymentCard, initializationAttempt]);

  const retryPaymentForm = () => {
    setErrorMessage('');
    setCanRetryPayment(false);
    setInitializationAttempt((attempt) => attempt + 1);
  };

  const plan = useMemo(() => {
    if (!checkoutData) return null;
    return PLAN_COPY[checkoutData.planCode] || PLAN_COPY.annual;
  }, [checkoutData]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!checkoutData || !sdkReady || !paymentCard) {
      setErrorMessage('The payment form is still loading. Please wait a moment.');
      return;
    }

    setIsSubmitting(true);
    setErrorMessage('');
    setStatusMessage('');

    try {
      const token = await paymentGateway.tokenize(paymentCard);

      const result = await createMembershipCheckout({
        first_name: checkoutData.firstName,
        last_name: checkoutData.lastName,
        email: checkoutData.email,
        phone: checkoutData.phone || null,
        plan_code: checkoutData.planCode,
        source_id: token,
        is_adult: checkoutData.isAdult,
        wants_promo: checkoutData.promo,
        wants_sms: checkoutData.sms,
      });

      if (result.token) {
        setAuthToken(result.token);
      }

      window.sessionStorage.removeItem(MEMBERSHIP_CHECKOUT_KEY);
      setStatusMessage('Membership activated. Redirecting you now...');
      navigate(checkoutData.redirect || '/profile', { replace: true });
    } catch (error) {
      setErrorMessage(error.message || 'We could not complete checkout.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!checkoutData || !plan) {
    return null;
  }

  return (
    <div className="auth-page">
      <div className="auth-card auth-card--wide">
        <div className="auth-header">
          <span className="badge">Secure Checkout</span>
          <h1>Complete your membership</h1>
          <p>Review your plan and enter your payment details to activate access.</p>
        </div>

        {statusMessage && (
          <div className="auth-alert auth-alert--success" role="status">{statusMessage}</div>
        )}
        {errorMessage && (
          <div className="auth-alert auth-alert--danger" role="alert">
            <span>{errorMessage}</span>
            {canRetryPayment && (
              <button type="button" className="secondary-button auth-retry-button" onClick={retryPaymentForm}>
                Retry secure payment form
              </button>
            )}
          </div>
        )}

        <div className="auth-checkout-grid">
          <section className="auth-plan-card">
            <h2>Order Summary</h2>
            <div className="auth-checkout-row">
              <span>{plan.name}</span>
              <strong>{plan.price}{plan.cadence}</strong>
            </div>
            <div className="auth-checkout-row auth-checkout-row--muted">
              <span>Name</span>
              <span>{checkoutData.firstName} {checkoutData.lastName}</span>
            </div>
            <div className="auth-checkout-row auth-checkout-row--muted">
              <span>Email</span>
              <span>{checkoutData.email}</span>
            </div>
            {checkoutData.phone && (
              <div className="auth-checkout-row auth-checkout-row--muted">
                <span>Phone</span>
                <span>{checkoutData.phone}</span>
              </div>
            )}
            <div className="auth-highlight">
              Your membership starts immediately after successful payment.
            </div>
            <Link className="secondary-button auth-inline-button" to="/register">
              Back to Membership Details
            </Link>
          </section>

          <form className="auth-plan-card auth-form" onSubmit={handleSubmit}>
            <h2>Payment Details</h2>
            <p className="auth-secondary-text">
              Powered by {paymentProvider === 'stripe' ? 'Stripe' : 'Square'}. Your card information is handled securely.
            </p>
            <div
              id="membership-card-container"
              className="auth-card-container"
              ref={cardContainerRef}
            />
            <button className="primary-button" type="submit" disabled={isSubmitting || !paymentCard}>
              {isSubmitting ? 'Processing…' : sdkReady ? `Pay ${plan.price}${plan.cadence}` : 'Loading Secure Form…'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default MembershipCheckoutPage;
